import { useMemo } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Pencil, Trash2 } from "lucide-react";
import moment from "moment-timezone";
import Button from "components/shared/Button";
import { useCompanyDateFormat } from "utils/hooks/company";
import { renderSettingsResourceLabel } from "utils/helpers/settings";
import { renderUserName } from "utils/helpers/user";

const columnHelper = createColumnHelper<any>();

interface ColumnProps {
    edit?: (row: any) => void;
    remove?: (id: string) => void;
}

export const useColumns = ({ edit, remove }: ColumnProps) => {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();

    return useMemo(() => {
        const columns: any[] = [
            columnHelper.accessor("startEffectiveDate", {
                header: t("start-effective-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
            columnHelper.accessor("endEffectiveDate", {
                header: t("end-effective-date"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
            columnHelper.accessor("override", {
                header: t("override"),
                cell: (info) => (info.getValue() ? t("yes") : t("no")),
            }),
            columnHelper.accessor("workingShifts", {
                header: t("working-shift"),
                cell: (info) => {
                    const value = info.getValue();
                    if (!Array.isArray(value) || value.length === 0) return "—";
                    return (
                        <div className="tw-flex tw-flex-wrap tw-gap-1">
                            {value.map((shift: any) => (
                                <span
                                    key={shift?.id}
                                    className="tw-border tw-border-primary tw-text-primary tw-rounded tw-px-2 tw-py-0.5 tw-text-xs"
                                >
                                    {renderSettingsResourceLabel(shift)}
                                </span>
                            ))}
                        </div>
                    );
                },
            }),
            columnHelper.accessor("createdBy", {
                header: t("created-by"),
                cell: (info) => renderUserName(info.getValue()) || "—",
            }),
            columnHelper.accessor("createdAt", {
                header: t("created-at"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
            columnHelper.accessor("updatedBy", {
                header: t("updated-by"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? renderUserName(value) : "—";
                },
            }),
            columnHelper.accessor("updatedAt", {
                header: t("updated-at"),
                cell: (info) => {
                    const value = info.getValue();
                    return value ? moment(value).format(dateFormat) : "—";
                },
            }),
        ];

        if (edit || remove) {
            columns.push(
                columnHelper.display({
                    id: "actions",
                    enableHiding: false,
                    enableColumnFilter: false,
                    header: () => <div className="tw-text-right">{t("actions")}</div>,
                    cell: ({ row: { original } }) => (
                        <div className="tw-flex tw-gap-1 tw-justify-end">
                            {edit && (
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => edit(original)}
                                    tooltip={t("edit")}
                                >
                                    <Pencil className="tw-h-4 tw-w-4" />
                                </Button>
                            )}
                            {remove && (
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => remove(original.id)}
                                    tooltip={t("delete")}
                                >
                                    <Trash2 className="tw-h-4 tw-w-4" />
                                </Button>
                            )}
                        </div>
                    ),
                })
            );
        }

        return columns;
    }, [t, dateFormat, edit, remove]);
};
